import React, { useState } from 'react';
import axios from 'axios';
import '../../styles/SchemaGenerator.css'; // Make sure this path is correct

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const CheckTablesQueryGenerator = () => {
    const [file, setFile] = useState(null);
    const [schemaFiles, setSchemaFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState("");
    const [report, setReport] = useState(null);
    const [script, setScript] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [fileName, setFileName] = useState("Sp_CheckTables_INIT.sql");

    // Fetch available schema files on load
    React.useEffect(() => {
        fetchSchemaFiles();
    }, []);

    const fetchSchemaFiles = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/dbupdate/schema/files`);
            setSchemaFiles(res.data.files || []);
        } catch (error) {
            console.error("Failed to fetch schema files", error);
        }
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!file) {
            setMessage("Please select a file to upload first.");
            return;
        }
        setIsUploading(true);
        setMessage("Uploading schema...");
        
        const formData = new FormData();
        formData.append('schemaFile', file);

        try {
            await axios.post(`${API_BASE_URL}/dbupdate/schema/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setMessage(`Successfully uploaded ${file.name} to server/Schema folder.`);
            setFile(null); // clear selection
            const fileInput = document.getElementById('schemaFileInput');
            if (fileInput) fileInput.value = ""; // clear input
            fetchSchemaFiles(); // refresh list
        } catch (error) {
            console.error("Upload Error:", error);
            setMessage(`Upload failed: ${error.response?.data?.error || error.message}`);
        } finally {
            setIsUploading(false);
        }
    }

    const handleAnalyze = async () => {
        if (!selectedFile) {
            setMessage("Please select a schema file from the list to analyze.");
            return;
        }

        setIsAnalyzing(true);
        setMessage(`Analyzing ${selectedFile}...`);

        try {
            const response = await axios.post(`${API_BASE_URL}/dbupdate/schema/analyze`, {
                filename: selectedFile
            });
            setReport(response.data.report);
            setScript(response.data.generatedScript);
            setMessage("Analysis complete! Review the changes before saving.");
        } catch (error) {
            console.error("Analysis Error:", error);
            setMessage(`Analysis failed: ${error.response?.data?.error || error.message}`);
            setReport(null);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleSave = async () => {
        if (!script) {
            setMessage("No script to save. Please analyze a schema file first.");
            return;
        }

        setIsSaving(true);
        setMessage("Saving script...");

        try {
            const response = await axios.post(`${API_BASE_URL}/dbupdate/schema/save`, {
                scriptContent: script,
                filename: fileName
            });
            setMessage(`? Success! Script verified and saved to server/Update/CheckTables as: ${response.data.filename || "new file"}`);
        } catch (error) {
            console.error("Save Error:", error);
            setMessage(`Save failed: ${error.response?.data?.error || error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[99999] bg-[#05070a] text-gray-300 font-sans flex flex-col h-screen w-screen selection:bg-indigo-500/30">
            {/* Premium Header */}
            <header className="flex-none flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#1a1f2e] to-[#0a0c10] border-b border-white/5 shadow-2xl">
                <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20 text-white font-bold text-lg">
                        SC
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-white tracking-tight">CheckTables Query Generator</h1>
                        <p className="text-xs text-indigo-400 font-medium opacity-80 uppercase tracking-widest mt-0.5">Schema Analysis & Synchronization</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        className="px-4 h-10 flex items-center justify-center rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 hover:text-white hover:bg-indigo-500/30 transition-all font-semibold text-sm"
                        onClick={() => window.location.href = '/dbupdate'}
                    >
                        DB Update Manager
                    </button>
                    <button 
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:text-white hover:bg-red-500 transition-all font-bold"
                        onClick={() => window.history.back()}
                    >
                        &#x2715;
                    </button>
                </div>
            </header>

            <div className="flex-1 flex overflow-hidden min-h-0">
                {/* Left Sidebar: Controls */}
                <aside className="w-[350px] flex-none flex flex-col bg-[#0d1018] border-r border-white/5 min-h-0">
                    <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
                        {/* 1. Upload Section */}
                        <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden shadow-xl">
                            <div className="px-4 py-3 bg-white/[0.02] border-b border-white/5 flex items-center justify-between">
                                <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">1. Upload Schema</span>
                                <span className="text-xl leading-none">??</span>
                            </div>
                            <div className="p-4 space-y-4">
                                <p className="text-[10px] text-gray-500 leading-relaxed italic">Upload a .sql schema file to the server's Schema folder for analysis.</p>
                                <div className="relative group">
                                    <input 
                                        type="file" 
                                        accept=".sql" 
                                        onChange={handleFileChange} 
                                        id="schemaFileInput"
                                        className="hidden"
                                    />
                                    <label 
                                        htmlFor="schemaFileInput"
                                        className="w-full flex items-center justify-center gap-2 p-3 bg-black/40 border border-dashed border-white/10 rounded-xl cursor-pointer hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-all text-xs font-semibold text-gray-400 group-hover:text-gray-200"
                                    >
                                        {file ? (
                                            <span className="text-indigo-300 truncate max-w-[200px]">{file.name}</span>
                                        ) : (
                                            <><span>Select SQL File</span> <span className="text-xs opacity-40">(.sql)</span></>
                                        )}
                                    </label>
                                </div>
                                <button 
                                    className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 font-bold text-xs transition-all ${!file || isUploading ? 'bg-gray-700 text-gray-400 grayscale opacity-50 cursor-not-allowed shadow-none' : 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-lg shadow-indigo-600/20 active:scale-[0.98]'}`}
                                    onClick={handleUpload}
                                    disabled={!file || isUploading}
                                >
                                    {isUploading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : "Start Upload"}
                                </button>
                            </div>
                        </div>

                        {/* 2. Analysis Section */}
                        <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden shadow-xl">
                            <div className="px-4 py-3 bg-white/[0.02] border-b border-white/5 flex items-center justify-between">
                                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">2. Analyze Schema</span>
                                <span className="text-xl leading-none">??</span>
                            </div>
                            <div className="p-4 space-y-4">
                                <div className="relative">
                                    <select 
                                        className="w-full pl-3 pr-8 py-2.5 bg-black/60 border border-white/10 text-[10px] text-indigo-300 rounded-lg outline-none appearance-none cursor-pointer font-mono"
                                        value={selectedFile}
                                        onChange={(e) => {
                                            setSelectedFile(e.target.value);
                                            setReport(null);
                                            setScript("");
                                            setMessage("");
                                        }}
                                    >
                                        <option value="">-- Choose Schema File --</option>
                                        {schemaFiles.map((fname, idx) => (
                                            <option key={idx} value={fname}>{fname}</option>
                                        ))}
                                    </select>
                                    <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[10px] text-indigo-500 opacity-50 font-bold">?</div>
                                </div>
                                <button 
                                    className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 font-bold text-xs transition-all ${!selectedFile || isAnalyzing ? 'bg-gray-700 text-gray-400 grayscale opacity-50 cursor-not-allowed shadow-none' : 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-lg shadow-emerald-600/20 active:scale-[0.98]'}`}
                                    onClick={handleAnalyze}
                                    disabled={!selectedFile || isAnalyzing}
                                >
                                    {isAnalyzing ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : "Generate Check Query"}
                                </button>
                            </div>
                        </div>

                        {/* Status Message Area */}
                        {message && (
                            <div className={`p-3 rounded-xl border text-[10px] font-bold leading-relaxed ${message.includes('fail') || message.includes('Error') ? 'bg-red-500/5 border-red-500/20 text-red-400' : 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400'}`}>
                                {message.includes('Success') || message.includes('complete') ? '? ' : '? '}
                                {message}
                            </div>
                        )}
                    </div>
                </aside>

                {/* Main Content: Registry, Logs, Runner */}
                <main className="flex-1 flex flex-col bg-[#05070a] min-w-0">
                    <div className="flex-1 flex flex-col min-h-0">
                        {/* Tab-like navigation for Results */}
                        <div className="px-8 pt-6 pb-2 bg-gradient-to-b from-white/[0.02] to-transparent border-b border-white/5 flex-none items-end flex gap-4">
                            <h2 className="text-xs font-bold text-indigo-400 uppercase tracking-[0.2em] mb-2">Analysis Workspace</h2>
                            <div className="flex gap-2 mb-[-1px]">
                                <button className={`px-4 py-2 text-[10px] font-black rounded-t-xl border-x border-t transition-all ${script ? 'bg-[#0a0c10] border-white/10 text-white shadow-[-10px_-10px_20px_rgba(0,0,0,0.5)]' : 'text-gray-600 border-transparent cursor-not-allowed'}`}>
                                    SQUARE-SYNC ENGINE
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-hidden p-6 bg-[#0a0c10] flex flex-col gap-6">
                            {/* Top row: Summary Cards */}
                            {report && (
                                <div className="grid grid-cols-3 gap-4 flex-none">
                                    {[
                                        { label: 'Tables Identified', count: report.tables.length, color: 'text-indigo-400', bg: 'bg-indigo-500/5', border: 'border-indigo-500/20' },
                                        { label: 'Custom Types', count: report.types.length, color: 'text-purple-400', bg: 'bg-purple-500/5', border: 'border-purple-500/20' },
                                        { label: 'Triggers to Rebuild', count: report.triggers.length, color: 'text-orange-400', bg: 'bg-orange-500/5', border: 'border-orange-500/20' }
                                    ].map((stat, i) => (
                                        <div key={i} className={`p-4 rounded-2xl border ${stat.bg} ${stat.border} flex flex-col items-center justify-center shadow-lg`}>
                                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">{stat.label}</span>
                                            <span className={`text-2xl font-black ${stat.color}`}>{stat.count}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Bottom row: Report Details & Script Preview */}
                            <div className="flex-1 flex gap-6 min-h-0">
                                {/* Report Details Column */}
                                {report && (
                                    <div className="w-[300px] flex-none flex flex-col bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden shadow-2xl">
                                        <div className="px-4 py-3 bg-white/[0.02] border-b border-white/5 flex items-center justify-between">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Metadata Registry</span>
                                        </div>
                                        <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                                            {report.tables.length > 0 && (
                                                <div className="space-y-2">
                                                    <h4 className="text-[9px] font-black text-indigo-500/60 uppercase">Tables to Sync</h4>
                                                    <div className="space-y-1.5">
                                                        {report.tables.map((t, i) => (
                                                            <div key={i} className="p-2 bg-black/40 border border-white/5 rounded-lg">
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <span className="text-[10px] font-bold text-gray-300">{t.name}</span>
                                                                    <span className="text-[8px] bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded-full">{t.columns.length} cols</span>
                                                                </div>
                                                                <div className="text-[8px] text-gray-600 font-mono truncate">
                                                                    {t.columns.slice(0, 3).map(c => c.name).join(', ')}...
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Script Column */}
                                <div className={`flex-1 flex flex-col rounded-2xl overflow-hidden border border-white/5 shadow-2xl bg-[#030509] ${!script ? 'items-center justify-center opacity-10' : ''}`}>
                                    {!script ? (
                                        <div className="text-center">
                                            <span className="text-6xl mb-4 block">??</span>
                                            <span className="italic text-sm">Analyze a schema to view the sync script...</span>
                                        </div>
                                    ) : (
                                        <>
                                            <header className="flex items-center justify-between p-4 bg-white/[0.03] border-b border-white/5">
                                                <div className="flex items-center gap-3 flex-1 max-w-[400px]">
                                                    <span className="text-[10px] font-black text-indigo-500 uppercase tracking-tighter">Save As:</span>
                                                    <input 
                                                        type="text" 
                                                        value={fileName} 
                                                        onChange={(e) => setFileName(e.target.value)} 
                                                        className="flex-1 bg-black/80 border border-white/10 text-xs text-indigo-300 rounded-xl px-4 py-2 outline-none font-mono"
                                                        placeholder="Enter filename..."
                                                    />
                                                </div>
                                                <button 
                                                    onClick={handleSave}
                                                    disabled={isSaving}
                                                    className="px-6 py-2.5 text-[10px] font-black bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl shadow-lg shadow-emerald-600/20 transition-all active:scale-95 disabled:opacity-20 flex items-center gap-2"
                                                >
                                                    {isSaving ? "VERIFYING..." : "?? VERIFY & SAVE TO CLUSTER"}
                                                </button>
                                            </header>
                                            <div className="flex-1 relative overflow-hidden">
                                                <div className="absolute inset-0 p-6 overflow-y-auto custom-scrollbar bg-black/40">
                                                    <pre className="text-indigo-200/90 font-mono text-xs leading-relaxed">
                                                        <code>{script}</code>
                                                    </pre>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default CheckTablesQueryGenerator;
